<script setup lang="ts">
import { computed, type PropType, ref, SetupContext, useAttrs } from "vue";
import { CheckboxCheckmark, DesignVersion, Placement } from "@/models/types";

defineOptions({ name: "BcCheckbox", inheritAttrs: false });

const attrs: SetupContext["attrs"] = useAttrs();

/** Specifies two-way binding for the checkbox */
const modelValue = defineModel<any>({ default: undefined });

const props = defineProps({
  /** Specifies the checkbox's checkmark style */
  checkmark: {
    type: String as PropType<CheckboxCheckmark>,
    default: "square",
    validator: (value: any) => CheckboxCheckmark.isCheckboxCheckmark(value),
  },
  /** Specifies the checkbox's style */
  design: {
    type: String as PropType<DesignVersion>,
    default: "6",
    validator: (value: any) => DesignVersion.isDesignVersion(value),
  },
  /** Disables checkbox for selection */
  disabled: { type: Boolean, default: false },
  /** Specifies custom `false` value for the checkbox */
  falseValue: {
    type: [String, Number, Boolean, Function, Object, Array],
    default: false,
  },
  /** Specifies the label of the checkbox */
  label: { type: [String, Number, Boolean], default: undefined },
  /** Specifies the position of the checkbox's label */
  labelPlacement: {
    type: String as PropType<Placement>,
    default: "right",
    validator: (value: any) => Placement.isPlacement(value),
  },
  /** Specifies the name of the checkbox */
  name: { type: String },
  /** Specifies that checkbox is read-only */
  readonly: { type: Boolean, default: false },
  /** Makes the checkbox's border rounded */
  rounded: { type: Boolean, default: false },
  /** Specifies custom `true` value for the checkbox */
  trueValue: {
    type: [String, Number, Boolean, Function, Object, Array],
    default: true,
  },
  /** Specifies the value of the checkbox */
  value: {
    type: [String, Number, Boolean, Function, Object, Array],
  },
});

const emit = defineEmits<{
  (e: "change", payload: any): void;
  (e: "update:model-value", payload: any): void;
}>();

/** Component container reference */
const containerRef = ref<HTMLInputElement | null>(null);
/** Checkbox input reference */
const inputRef = ref<HTMLInputElement | null>(null);

/** Gets if the checkbox is checked based on the `true` and `false` values */
const isChecked = computed(() => {
  if (modelValue.value instanceof Array) {
    return modelValue.value.includes(props.value!);
  }
  return typeof modelValue.value !== "undefined" && modelValue.value !== null
    ? modelValue.value === props.trueValue
    : props.value === props.trueValue;
});
const rootClass = computed(() => (attrs.class ? (attrs.class as string).split(" ") : []));
const rootStyle = computed(() => attrs.style as any);
const inheritedAttrs = computed(() => {
  const { class: string, style: any, ...otherProps } = attrs;
  const inheritedProps = { ...otherProps };
  return inheritedProps;
});

const pak = computed<string>(() => {
  return "bc-controls-d" + props.design;
});

function focus(): void {
  // MacOS FireFox and Safari do not focus when clicked
  inputRef.value?.focus();
}

function updateInput(event: Event): void {
  if (props.readonly) {
    return;
  }

  let isChecked = (event.target as HTMLInputElement).checked;
  if (modelValue.value && modelValue.value instanceof Array) {
    let newValue = [...modelValue.value];
    if (isChecked) {
      newValue.push(props.value!);
    } else {
      newValue.splice(newValue.indexOf(props.value!), 1);
    }
    modelValue.value = newValue;
    emit("change", newValue);
  } else {
    modelValue.value = isChecked ? props.trueValue : props.falseValue;
    emit("change", isChecked ? props.trueValue : props.falseValue);
  }
}

defineExpose({
  modelValue,
  containerRef,
  inputRef,
  isChecked,
});
</script>

<template>
  <label
    :data-pak="pak"
    class="bissantzControls checkboxControl"
    :class="[
      `label-${labelPlacement}`,
      `checkmark-${checkmark}`,
      {
        disabled: disabled,
        readonly: readonly,
        rounded: rounded,
      },
      ...rootClass,
    ]"
    :style="rootStyle"
    ref="containerRef"
    :data-design="design"
    @click="focus"
    @keydown.prevent.enter="containerRef!.click()"
    @keydown.prevent.space="containerRef!.click()"
  >
    <input
      v-show="!readonly"
      type="checkbox"
      ref="inputRef"
      @click.stop
      :disabled="disabled"
      :readonly="readonly"
      :name="name"
      :checked="isChecked"
      :value="value"
      v-bind="inheritedAttrs"
      @change="updateInput"
    />
    <span
      class="checkboxLabel"
      :class="{ readonly: readonly }"
      :data-locid="$attrs['data-label-locid']"
      :data-locparam="$attrs['data-label-locparam']"
      :data-locparam1="$attrs['data-label-locparam1']"
      :data-locparam2="$attrs['data-label-locparam2']"
      ><slot>{{ label }}</slot></span
    >
  </label>
</template>

<style lang="less">
@import "../styles/components/bc-checkbox.less";
</style>
